import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { TemplateDTO } from '../dto/template'
import { Text, Title } from '@siftscience/focus-components/text'
import { useTemplateContext } from '../context/template-context'
import {
  ChevronRight,
  Checkmark,
  Close,
  Edit
} from '@siftscience/focus-components/icons'
import { TextInput } from '@siftscience/focus-components/input'
import { IconButton } from '@siftscience/focus-components/button'

const getParentList = (parentList: string) => {
  if (!parentList) {
    return []
  }

  return parentList
    ?.split('-')
    ?.map(id => +id)
    ?.reverse()
}

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: '36px'
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    marginRight: '8px',
    gap: '4px'
  },
  parentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  text: {
    marginRight: '4px'
  },
  icon: {
    width: '16px',
    height: '16px',
    marginRight: '4px'
  },
  editWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px'
  },
  editIcon: {
    width: '16px',
    height: '16px'
  },
  errorText: {
    color: '#EB3B2E',
    whiteSpace: 'nowrap'
  }
}))

interface TemplateHeaderProps {
  template: TemplateDTO
}

const TemplateHeader = ({
  template
}: TemplateHeaderProps): React.ReactElement => {
  const {
    templatesHash,
    parentList,
    setUpdatedTemplateName,
    updatedTemplateName
  } = useTemplateContext()
  const classes = useStyles()
  const parentIds = getParentList(parentList)
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState(template?.displayName || '')
  const hasError = name.trim() === ''

  const handleCancel = () => {
    setName(template?.displayName)
    setUpdatedTemplateName(null)
    setIsEditing(false)
  }

  useEffect(() => {
    setName(template?.displayName || '')
    setIsEditing(false)
  }, [template])

  const handleSave = () => {
    const trimmedName = name.trim()
    if (trimmedName) {
      if (trimmedName !== template.displayName) {
        setUpdatedTemplateName(trimmedName)
      } else {
        setUpdatedTemplateName(null)
      }
    } else {
      handleCancel()
      return
    }
    setIsEditing(false)
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value
    setName(newName)
    setUpdatedTemplateName(newName)
  }

  return (
    <div className={classes.wrapper}>
      {isEditing ? (
        <div className={classes.editWrapper}>
          <TextInput
            value={name}
            onChange={handleNameChange}
            onKeyDown={handleKeyDown}
            autoFocus
            onBlur={handleSave}
            error={hasError}
          />
          {hasError ? (
            <Text className={classes.errorText} size="small">
              Can't allow template name to be empty
            </Text>
          ) : (
            <IconButton
              onClick={handleSave}
              variant="secondary"
              lined
              onMouseDown={e => e.preventDefault()}
            >
              <Checkmark color="#0AA636" />
            </IconButton>
          )}

          <IconButton
            onClick={handleCancel}
            variant="secondary"
            lined
            onMouseDown={e => e.preventDefault()}
          >
            <Close color="#EB3B2E" />
          </IconButton>
        </div>
      ) : (
        <div className={classes.titleWrapper} onClick={handleEdit}>
          <Title size="large">
            {updatedTemplateName ?? template?.displayName}
          </Title>
          <Edit className={classes.editIcon} color="#B3B3B3" />
        </div>
      )}
      {!isEditing &&
        parentIds?.map(id => (
          <div key={id} className={classes.parentWrapper}>
            <ChevronRight className={classes.icon} color="#B3B3B3" />
            <Text className={classes.text} size="xsmall">
              {templatesHash.get(id)?.displayName}
            </Text>
          </div>
        ))}
    </div>
  )
}

export default TemplateHeader
